import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  FileUpload as FileIcon,
} from '@mui/icons-material';
import { uploadLeadStages } from '../../../api/leadStage.api.js';

function LeadStageImport({ onClose, onImportComplete }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError('');
      setResult(null);
    } else {
      setError('Please select a valid CSV file.');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const result = await uploadLeadStages(file);

      clearInterval(progressInterval);
      setUploadProgress(100);
      setResult(result);

      if (result.success) {
        setTimeout(() => {
          onImportComplete && onImportComplete(result);
        }, 2000);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = [
      ['name', 'description', 'color', 'order'],
      ['New Lead', 'Initial contact made, basic information collected', 'primary', '1'],
      ['Qualified Lead', 'Lead has shown interest and meets basic criteria', 'info', '2'],
      ['Proposal Sent', 'Proposal or quote has been sent to the lead', 'warning', '3'],
      ['Negotiation', 'In discussion about terms, pricing, or requirements', 'secondary', '4'],
      ['Closed Won', 'Deal successfully closed and won', 'success', '5'],
      ['Closed Lost', 'Deal lost to competition or other reasons', 'error', '6'],
    ].map((row) => row.map((field) => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lead_stages_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setFile(null);
    setUploading(false);
    setUploadProgress(0);
    setResult(null);
    setError('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6" component="h2">
          Import Lead Stages
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {!result ? (
        <Stack spacing={3}>
          {/* File Selection */}
          <Paper sx={{ p: 3, border: '2px dashed', borderColor: 'grey.300' }}>
            <Stack spacing={2} alignItems="center">
              <FileIcon sx={{ fontSize: 48, color: 'grey.500' }} />
              <Typography variant="h6" color="text.secondary">
                Select CSV File
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Upload a CSV file with lead stage data. The file should include columns for name, description, color, and order.
              </Typography>

              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                disabled={uploading}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={handleFileSelect}
                />
              </Button>

              {file && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="primary">
                    Selected:
                    {' '}
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Size:
                    {' '}
                    {(file.size / 1024).toFixed(1)}
                    {' '}
                    KB
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Template Download */}
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Don't have a template?
              </Typography>
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
              >
                Download Template
              </Button>
            </Stack>
          </Paper>

          {/* Upload Progress */}
          {uploading && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Uploading...
                {' '}
                {uploadProgress}
                %
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!file || uploading}
              startIcon={<UploadIcon />}
            >
              {uploading ? 'Uploading...' : 'Upload Lead Stages'}
            </Button>
          </Stack>
        </Stack>
      ) : (
        /* Results Display */
        <Stack spacing={3}>
          <Alert
            severity={result.success ? 'success' : 'error'}
            icon={result.success ? <SuccessIcon /> : <ErrorIcon />}
          >
            <Typography variant="h6" gutterBottom>
              {result.success ? 'Import Completed!' : 'Import Failed'}
            </Typography>
            <Typography variant="body2">
              {result.success
                ? `Successfully imported ${result.imported} lead stages from ${result.totalRows} rows.`
                : 'There were errors during the import process.'}
            </Typography>
          </Alert>

          {/* Import Summary */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Import Summary
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Total Rows Processed:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {result.totalRows}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Successfully Imported:</Typography>
                <Typography variant="body2" fontWeight="medium" color="success.main">
                  {result.imported}
                </Typography>
              </Stack>
              {result.errors && result.errors.length > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Errors:</Typography>
                  <Typography variant="body2" fontWeight="medium" color="error.main">
                    {result.errors.length}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Paper>

          {/* Error Details */}
          {result.errors && result.errors.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Error Details
              </Typography>
              <List dense>
                {result.errors.map((error, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <ErrorIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={error}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={resetForm}>
              Import Another File
            </Button>
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

export default LeadStageImport;
